import React, { useEffect, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { trans } from 'sharedKernel/i18n'
import { Loading } from 'components/loading'
import { IMeetingRoomSolicitationDetail } from '../types'
import {
  ACCESS_TIME_ICON_URL,
  CancelButton,
  ConfirmButton,
  FieldGroup,
  FieldLabel,
  FieldRow,
  FieldRowGrid,
  FooterActions,
  FormFieldsContainer,
  Section,
  SectionTitle,
  StyledModal,
  TimeInputWrapper,
} from './styles'

export interface MeetingRoomSpaceOption {
  id: string
  name: string
}

interface EditReservationFormData {
  meetingRoomId: string
  roomName: string
  startTime: string
  endTime: string
}

interface EditReservationModalProps {
  isOpen: boolean
  solicitation: IMeetingRoomSolicitationDetail | null
  spaceOptions: MeetingRoomSpaceOption[]
  isSubmitting?: boolean
  isLoading?: boolean
  onClose: () => void
  onConfirm: (solicitationId: string, data: EditReservationFormData) => void
}

const parseTimeRange = (timeRange: string): { startTime: string; endTime: string } => {
  const [startTime = '', endTime = ''] = timeRange.split(' - ').map(value => value.trim())
  return { startTime, endTime }
}

const getReservationTimes = (
  solicitation: IMeetingRoomSolicitationDetail
): { startTime: string; endTime: string } => {
  const fromFields = {
    startTime: solicitation.startTime?.trim() ?? '',
    endTime: solicitation.endTime?.trim() ?? '',
  }

  if (fromFields.startTime && fromFields.endTime) {
    return fromFields
  }

  return parseTimeRange(solicitation.timeRange)
}

const toTimeInputValue = (time: string): string => {
  if (!time) return ''
  const [hours, minutes] = time.split(':')
  if (!hours || !minutes) return time
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
}

const TimeInputIcon = (): JSX.Element => (
  <img className="time-input-icon" src={ACCESS_TIME_ICON_URL} alt="" aria-hidden />
)

export const EditReservationModal = ({
  isOpen,
  solicitation,
  spaceOptions,
  isSubmitting = false,
  isLoading = false,
  onClose,
  onConfirm,
}: EditReservationModalProps): JSX.Element => {
  const currentReservation = solicitation ? getReservationTimes(solicitation) : null

  const [newMeetingRoomId, setNewMeetingRoomId] = useState('')
  const [newStartTime, setNewStartTime] = useState('')
  const [newEndTime, setNewEndTime] = useState('')

  useEffect(() => {
    if (!solicitation) {
      setNewMeetingRoomId('')
      setNewStartTime('')
      setNewEndTime('')
      return
    }

    const { startTime, endTime } = getReservationTimes(solicitation)
    setNewMeetingRoomId(solicitation.meetingRoomId || '')
    setNewStartTime(toTimeInputValue(startTime))
    setNewEndTime(toTimeInputValue(endTime))
  }, [solicitation])

  const handleConfirm = (): void => {
    if (!solicitation || !newMeetingRoomId || !newStartTime || !newEndTime) {
      return
    }

    const selectedRoom = spaceOptions.find(option => option.id === newMeetingRoomId)
    if (!selectedRoom) return

    onConfirm(solicitation.id, {
      meetingRoomId: selectedRoom.id,
      roomName: selectedRoom.name,
      startTime: newStartTime,
      endTime: newEndTime,
    })
  }

  const isFormValid = Boolean(newMeetingRoomId && newStartTime && newEndTime)

  return (
    <StyledModal show={isOpen} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{trans('meetingRoom.reservations.edit.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <Loading />
        ) : (
          solicitation &&
          currentReservation && (
            <FormFieldsContainer>
            <Section>
              <SectionTitle>{trans('meetingRoom.reservations.edit.currentSection')}</SectionTitle>
              <FieldGroup>
                <FieldRow>
                  <FieldLabel>{trans('meetingRoom.reservations.edit.currentSpace')}</FieldLabel>
                  <Form.Control readOnly value={solicitation.space} />
                </FieldRow>

                <FieldRowGrid>
                  <FieldRow>
                    <FieldLabel>{trans('meetingRoom.reservations.edit.startTime')}</FieldLabel>
                    <TimeInputWrapper $readonly>
                      <TimeInputIcon />
                      <Form.Control readOnly value={currentReservation.startTime} />
                    </TimeInputWrapper>
                  </FieldRow>
                  <FieldRow>
                    <FieldLabel>{trans('meetingRoom.reservations.edit.endTime')}</FieldLabel>
                    <TimeInputWrapper $readonly>
                      <TimeInputIcon />
                      <Form.Control readOnly value={currentReservation.endTime} />
                    </TimeInputWrapper>
                  </FieldRow>
                </FieldRowGrid>
              </FieldGroup>
            </Section>

            <Section>
              <SectionTitle>{trans('meetingRoom.reservations.edit.newSection')}</SectionTitle>
              <FieldGroup>
                <FieldRow>
                  <FieldLabel>{trans('meetingRoom.reservations.edit.newSpace')}</FieldLabel>
                  <Form.Select
                    value={newMeetingRoomId}
                    onChange={event => setNewMeetingRoomId(event.target.value)}
                  >
                    <option value="" disabled>
                      {trans('meetingRoom.purpose.select')}
                    </option>
                    {spaceOptions.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </Form.Select>
                </FieldRow>

                <FieldRowGrid>
                  <FieldRow>
                    <FieldLabel>{trans('meetingRoom.reservations.edit.newStartTime')}</FieldLabel>
                    <TimeInputWrapper>
                      <TimeInputIcon />
                      <Form.Control
                        type="time"
                        value={newStartTime}
                        onChange={event => setNewStartTime(event.target.value)}
                      />
                    </TimeInputWrapper>
                  </FieldRow>
                  <FieldRow>
                    <FieldLabel>{trans('meetingRoom.reservations.edit.newEndTime')}</FieldLabel>
                    <TimeInputWrapper>
                      <TimeInputIcon />
                      <Form.Control
                        type="time"
                        value={newEndTime}
                        onChange={event => setNewEndTime(event.target.value)}
                      />
                    </TimeInputWrapper>
                  </FieldRow>
                </FieldRowGrid>

                <FooterActions>
                  <ConfirmButton
                    type="button"
                    onClick={handleConfirm}
                    disabled={!isFormValid || isSubmitting}
                  >
                    {trans('meetingRoom.reservations.edit.confirm')}
                  </ConfirmButton>
                  <CancelButton type="button" onClick={onClose}>
                    {trans('meetingRoom.reservations.edit.cancel')}
                  </CancelButton>
                </FooterActions>
              </FieldGroup>
            </Section>
          </FormFieldsContainer>
          )
        )}
      </Modal.Body>
    </StyledModal>
  )
}
